import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

function useScrollReveal() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); observer.disconnect(); } },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return { ref, visible };
}

function RevealDiv({ children, className = '', delay = 0 }: { children: React.ReactNode; className?: string; delay?: number }) {
  const { ref, visible } = useScrollReveal();
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(36px)',
        transition: `opacity 0.65s ease ${delay}ms, transform 0.65s ease ${delay}ms`,
      }}
    >
      {children}
    </div>
  );
}

const FOUNDER_IMAGE = 'https://res.cloudinary.com/dxit9ltm4/image/upload/v1776002909/clothes_change_ai_1775988373398_e7b5gg.jpg';

const expertiseAreas = [
  { icon: 'ri-robot-line', title: 'AI & IT', description: 'Implementing artificial intelligence solutions to streamline operations and drive growth.' },
  { icon: 'ri-code-s-slash-line', title: 'Software Development', description: 'Building custom software applications tailored to business needs.' },
  { icon: 'ri-global-line', title: 'Website Development', description: 'Creating responsive, SEO-optimized websites that convert visitors into customers.' },
  { icon: 'ri-bar-chart-2-line', title: 'Digital Marketing', description: 'Strategic online marketing campaigns that increase visibility and revenue.' },
  { icon: 'ri-settings-3-line', title: 'Business Automation', description: 'Automating repetitive tasks and workflows to save time and reduce costs.' },
  { icon: 'ri-service-line', title: 'IT Consulting', description: 'Advisory services to help businesses select and implement the right technology stack.' },
];

const articles = [
  { title: 'AI for Ugandan Businesses', category: 'Artificial Intelligence' },
  { title: 'Digital Transformation for SMEs', category: 'Business Growth' },
  { title: 'Website Development Best Practices', category: 'Web Development' },
  { title: 'AI Automation for Local Enterprises', category: 'Automation' },
  { title: 'Software Development in East Africa', category: 'Technology' },
  { title: 'The Future of AI in East Africa', category: 'AI Trends' },
  { title: 'Construction Management Software', category: 'Industry Solutions' },
];

export default function BienvenuPage() {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-slate-900 via-blue-900 to-teal-800 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/30"></div>
        <div className="absolute -top-20 -left-20 w-96 h-96 bg-teal-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute -bottom-20 -right-20 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1.2s' }}></div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center text-white">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-sm font-medium mb-6">
              <span>Professional Profile</span>
            </div>
            <h1 className="font-display text-5xl md:text-6xl font-bold mb-6" style={{ animation: 'fadeSlideDown 0.8s ease both' }}>
              Bienvenu Masengesho
            </h1>
            <p className="text-xl md:text-2xl text-gray-200 leading-relaxed mb-8" style={{ animation: 'fadeSlideDown 0.8s ease 0.2s both' }}>
              Founder & CEO of BientoMart
            </p>
            <p className="text-lg text-gray-300 leading-relaxed max-w-2xl mx-auto mb-10" style={{ animation: 'fadeSlideDown 0.8s ease 0.3s both' }}>
              Driving digital innovation across Uganda and East Africa through AI, software development, and strategic marketing.
            </p>
            <div className="flex flex-wrap gap-4 justify-center" style={{ animation: 'fadeSlideDown 0.8s ease 0.4s both' }}>
              <Link
                to="/services"
                className="inline-flex items-center gap-2 px-8 py-4 bg-clairBlue-300 hover:bg-clairBlue-200 text-darkBlue-500 font-bold text-lg rounded-xl transition-all hover:scale-105 shadow-xl"
              >
                Explore Services
                <i className="ri-arrow-right-line"></i>
              </Link>
              <a
                href="https://wa.me/256773515447"
                rel="nofollow"
                target="_blank"
                className="inline-flex items-center gap-2 px-8 py-4 bg-white/10 hover:bg-white/20 border border-white/30 text-white font-bold text-lg rounded-xl backdrop-blur-sm transition-all hover:scale-105"
              >
                <i className="ri-whatsapp-line"></i>
                Chat on WhatsApp
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Profile Image + Bio */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <RevealDiv>
              <div className="relative">
                <div className="absolute -top-4 -left-4 w-24 h-24 bg-teal-500/20 rounded-full blur-2xl"></div>
                <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-blue-500/20 rounded-full blur-2xl"></div>
                <img
                  src={FOUNDER_IMAGE}
                  alt="Bienvenu Masengesho"
                  className="relative rounded-3xl shadow-2xl w-full h-auto"
                />
              </div>
            </RevealDiv>
            <RevealDiv delay={200}>
              <h2 className="font-display text-4xl font-bold text-gray-900 mb-6">
                Professional Profile
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                Bienvenu Masengesho is the Founder & CEO of BientoMart (also known as Asante Media Agency) and the Bridgecore empowerment initiative. Based in Kampala, Uganda, he leads a team focused on delivering cutting-edge digital solutions to businesses across East Africa.
              </p>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                With expertise spanning AI & IT, software development, website development, digital marketing, business automation, and IT consulting, Bienvenu bridges the gap between emerging technology and practical business applications.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link
                  to="/portfolio"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-darkBlue-500 hover:bg-darkBlue-600 text-white font-bold rounded-xl transition-all hover:scale-105"
                >
                  View Projects
                  <i className="ri-arrow-right-line"></i>
                </Link>
                <a
                  href="https://wa.me/256773515447"
                  rel="nofollow"
                  target="_blank"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-clairBlue-300 hover:bg-clairBlue-200 text-darkBlue-500 font-bold rounded-xl transition-all hover:scale-105"
                >
                  <i className="ri-whatsapp-line"></i>
                  Book Consultation
                </a>
              </div>
            </RevealDiv>
          </div>
        </div>
      </section>

      {/* Expertise Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <RevealDiv>
            <div className="max-w-4xl mx-auto text-center mb-16">
              <h2 className="font-display text-4xl font-bold text-gray-900 mb-6">Areas of Expertise</h2>
              <p className="text-xl text-gray-600">Specialized skills to help businesses thrive in the digital economy</p>
            </div>
          </RevealDiv>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {expertiseAreas.map((area, index) => (
              <RevealDiv key={index} delay={index * 100}>
                <div className="bg-white rounded-2xl p-8 border border-gray-100 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-2 group h-full">
                  <div className="w-14 h-14 flex items-center justify-center bg-gradient-to-br from-blue-600 to-teal-500 rounded-2xl mb-6 group-hover:scale-110 transition-transform duration-300">
                    <i className={`${area.icon} text-2xl text-white`}></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{area.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{area.description}</p>
                </div>
              </RevealDiv>
            ))}
          </div>
        </div>
      </section>

      {/* Projects Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <RevealDiv>
            <div className="max-w-4xl mx-auto text-center mb-16">
              <h2 className="font-display text-4xl font-bold text-gray-900 mb-6">Selected Projects</h2>
              <p className="text-xl text-gray-600">A glimpse into work delivered through BientoMart</p>
            </div>
          </RevealDiv>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                title: 'AL-WASILAH ISLAMIC INSTITUTE',
                category: 'Education',
                image: 'https://res.cloudinary.com/dodsf90se/image/upload/v1772981142/alwas_fqnej9.png',
                link: 'https://www.alwasilahislamicinstitute.org/',
              },
              {
                title: 'HEAVEN COMMUNITY PATHWAY',
                category: 'Organisation',
                image: 'https://res.cloudinary.com/dodsf90se/image/upload/v1772983821/heaven_n6cosh.png',
                link: 'https://www.heavencommunitypathway.org/',
              },
              {
                title: 'VIMATEC LTD',
                category: 'Technology',
                image: 'https://res.cloudinary.com/dodsf90se/image/upload/v1772983956/vimatk_e5szd9.png',
                link: 'https://vimatec-ltd.vercel.app/',
              },
            ].map((project, index) => (
              <RevealDiv key={index} delay={index * 100}>
                <Link
                  to={project.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block bg-white rounded-2xl overflow-hidden border border-gray-100 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-2 group"
                >
                  <div className="relative w-full h-56 overflow-hidden">
                    <img
                      src={project.image}
                      alt={project.title}
                      className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  </div>
                  <div className="p-6">
                    <span className="text-xs font-semibold text-clairBlue-500 uppercase tracking-wider">{project.category}</span>
                    <h3 className="text-lg font-bold text-gray-900 mt-2">{project.title}</h3>
                  </div>
                </Link>
              </RevealDiv>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link
              to="/portfolio"
              className="inline-flex items-center gap-2 px-8 py-4 bg-darkBlue-500 hover:bg-darkBlue-600 text-white font-bold rounded-xl transition-all hover:scale-105"
            >
              View All Projects
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Articles Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <RevealDiv>
            <div className="max-w-4xl mx-auto text-center mb-16">
              <h2 className="font-display text-4xl font-bold text-gray-900 mb-6">Articles & Insights</h2>
              <p className="text-xl text-gray-600">Thoughts on AI, software development, and digital transformation</p>
            </div>
          </RevealDiv>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {articles.map((article, index) => (
              <RevealDiv key={index} delay={index * 100}>
                <div className="bg-white rounded-2xl p-8 border border-gray-100 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-2 group h-full flex flex-col">
                  <span className="text-xs font-semibold text-clairBlue-500 uppercase tracking-wider mb-4">{article.category}</span>
                  <h3 className="text-xl font-bold text-gray-900 mb-4 flex-1">{article.title}</h3>
                  <span className="inline-flex items-center gap-2 text-darkBlue-500 font-semibold text-sm group-hover:gap-3 transition-all">
                    Read Article <i className="ri-arrow-right-line"></i>
                  </span>
                </div>
              </RevealDiv>
            ))}
          </div>
        </div>
      </section>

      {/* Social & CTA Section */}
      <section className="py-20 bg-darkBlue-500 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-full h-full"
            style={{
              backgroundImage: 'radial-gradient(circle at 20% 50%, #7EC8E3 1px, transparent 1px), radial-gradient(circle at 80% 50%, #7EC8E3 1px, transparent 1px)',
              backgroundSize: '30px 30px'
            }}
          ></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <RevealDiv>
              <h2 className="font-display text-4xl md:text-5xl font-bold text-white mb-6">
                Let's Build Something Great Together
              </h2>
              <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                Whether you need a new website, AI automation, or a complete digital strategy, I'm here to help your business grow.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link
                  to="/services"
                  className="inline-flex items-center gap-2 px-8 py-4 bg-clairBlue-300 hover:bg-clairBlue-200 text-darkBlue-500 font-bold text-lg rounded-xl transition-all hover:scale-105 shadow-xl"
                >
                  Start a Project
                  <i className="ri-arrow-right-line"></i>
                </Link>
                <a
                  href="https://wa.me/256773515447"
                  rel="nofollow"
                  target="_blank"
                  className="inline-flex items-center gap-2 px-8 py-4 bg-white/10 hover:bg-white/20 border border-white/30 text-white font-bold text-lg rounded-xl backdrop-blur-sm transition-all hover:scale-105"
                >
                  <i className="ri-whatsapp-line"></i>
                  Chat on WhatsApp
                </a>
              </div>
            </RevealDiv>
            <RevealDiv delay={200}>
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-3xl p-8">
                <h3 className="font-display text-2xl font-bold text-white mb-6">Connect With Me</h3>
                <div className="space-y-4">
                  {[
                    { icon: 'ri-twitter-x-line', label: 'X / Twitter', href: 'https://www.x.com/M97Bienvenu' },
                    { icon: 'ri-linkedin-box-line', label: 'LinkedIn', href: 'https://www.linkedin.com/company/112266406' },
                    { icon: 'ri-instagram-line', label: 'Instagram', href: 'https://www.instagram.com/bientomart/?hl=en' },
                    { icon: 'ri-facebook-circle-line', label: 'Facebook', href: 'https://www.facebook.com/profile.php?id=61584207860930' },
                    { icon: 'ri-tiktok-line', label: 'TikTok', href: 'https://www.tiktok.com/@bientomart' },
                  ].map((social) => (
                    <a
                      key={social.label}
                      href={social.href}
                      rel="nofollow"
                      target="_blank"
                      className="flex items-center gap-4 text-gray-300 hover:text-clairBlue-300 transition-colors"
                    >
                      <div className="w-10 h-10 flex items-center justify-center bg-white/10 rounded-lg">
                        <i className={`${social.icon} text-lg`}></i>
                      </div>
                      <span className="font-medium">{social.label}</span>
                      <i className="ri-arrow-right-up-line ml-auto"></i>
                    </a>
                  ))}
                </div>
              </div>
            </RevealDiv>
          </div>
        </div>
      </section>

      {/* Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Person',
            name: 'Bienvenu Masengesho',
            jobTitle: 'Founder & CEO',
            worksFor: {
              '@type': 'Organization',
              name: 'BientoMart',
              alternateName: 'Asante Media Agency',
            },
            url: 'https://www.bientomart.com/bienvenu-masengesho',
            knowsAbout: ['Artificial Intelligence', 'Software Development', 'Website Development', 'Digital Marketing', 'Business Automation', 'IT Consulting'],
            sameAs: [
              'https://www.x.com/M97Bienvenu',
              'https://www.linkedin.com/company/112266406',
              'https://www.instagram.com/bientomart/?hl=en',
              'https://www.facebook.com/profile.php?id=61584207860930',
              'https://www.tiktok.com/@bientomart',
            ],
          }),
        }}
      />

      <Footer />

      <style>{`
        @keyframes fadeSlideDown {
          from { opacity: 0; transform: translateY(-28px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
